// The backend client — and the single home of the retry / nonce discipline. This is the
// most correctness-critical code in the SPA. Get it wrong and a dropped network packet
// reads to the player as "my 50 FS vanished."
//
// The invariants (from BUILD_HANDOFF_arcade + verified against hub-core core/ledger.py):
//
//  * settle_spin is idempotent on spin_id = (game_id, uid, epoch, nonce), and it checks
//    that replay BEFORE the nonce. So retrying the SAME (epoch, expected_nonce) after a
//    lost response is always safe: if the first settle landed, hub-core replays it and
//    returns the same outcome (replayed=true); if it didn't, it settles fresh.
//  * Therefore a dropped settle (network error / 502 / 504 / 429) is retried with the
//    SAME (epoch, expected_nonce). NEVER fetch a fresh nonce to "try again" — that would
//    start a second, different spin.
//  * A fresh nonce is fetched ONLY on an explicit 200 status=NONCE_MISMATCH (the nonce was
//    genuinely stale, and no charge occurred). REVEALED_SEED / STALE_SEED transparently
//    re-fetch /config and retry. Everything else is returned to the caller as-is.

import { API_BASE, GAME_ID } from './config';
import type { BalanceResponse, ErrorBody, Settlement, SeedRotation, SpinConfig } from './types';

const MAX_TRANSPORT_RETRIES = 3; // outer retries on top of the backend's own idempotent retries
const MAX_STALE_RETRIES = 2; // REVEALED_SEED / STALE_SEED transparent re-fetch+retry
const MAX_NONCE_RETRIES = 1; // NONCE_MISMATCH re-fetch+retry (once)
const RETRY_AFTER_CAP_S = 10;

export class AuthError extends Error {}
export class UpstreamUnavailableError extends Error {
  // Carries the settle body so the caller can offer a manual "retry the SAME spin".
  constructor(public readonly retrySame: () => Promise<Settlement>) {
    super('upstream_unavailable');
  }
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));
const backoffMs = (attempt: number) => Math.min(2000, 250 * 2 ** attempt);

export interface ApiHooks {
  onReconnecting?: (attempt: number) => void;
  onReconnected?: () => void;
}

export class ArcadeApi {
  constructor(
    private accessToken: string,
    private hooks: ApiHooks = {},
  ) {}

  private authHeaders(): HeadersInit {
    return {
      Authorization: `Bearer ${this.accessToken}`,
      'Content-Type': 'application/json',
    };
  }

  async getConfig(): Promise<SpinConfig> {
    const res = await fetch(`${API_BASE}/config?game_id=${encodeURIComponent(GAME_ID)}`, {
      headers: this.authHeaders(),
    });
    if (res.status === 401) throw new AuthError('unauthorized');
    if (!res.ok) throw new Error(`config failed (${res.status})`);
    return (await res.json()) as SpinConfig;
  }

  async getBalance(): Promise<number> {
    const res = await fetch(`${API_BASE}/balance`, { headers: this.authHeaders() });
    if (res.status === 401) throw new AuthError('unauthorized');
    if (!res.ok) throw new Error(`balance failed (${res.status})`);
    const body = (await res.json()) as BalanceResponse;
    return body.balance;
  }

  async rotateSeed(newClientSeed: string): Promise<SeedRotation> {
    // NOT transport-retried: rotate_seed is non-idempotent upstream (a retried rotation
    // double-rotates, burning two epochs). One shot; surface failure to the caller.
    const res = await fetch(`${API_BASE}/spin/rotate_seed`, {
      method: 'POST',
      headers: this.authHeaders(),
      body: JSON.stringify({ new_client_seed: newClientSeed, game_id: GAME_ID }),
    });
    if (res.status === 401) throw new AuthError('unauthorized');
    if (!res.ok) throw new Error(`rotate_seed failed (${res.status})`);
    return (await res.json()) as SeedRotation;
  }

  /** Paid spin. Handles the full retry/nonce discipline; returns a terminal Settlement. */
  settle(epoch: number, expectedNonce: number): Promise<Settlement> {
    return this.settleWithStatusHandling(
      () => ({ epoch, expected_nonce: expectedNonce, game_id: GAME_ID }),
      '/spin/settle',
      0,
      0,
    );
  }

  /** Free (ticket) spin. epoch/expected_nonce are all-or-nothing; omit both to let hub-core
   *  pin the current config. interaction_id is the idempotency unit for ticket spins. */
  ticketSpend(interactionId: string, epoch?: number, expectedNonce?: number): Promise<Settlement> {
    const build = () => {
      const body: Record<string, unknown> = { interaction_id: interactionId, game_id: GAME_ID };
      if (epoch !== undefined && expectedNonce !== undefined) {
        body.epoch = epoch;
        body.expected_nonce = expectedNonce;
      }
      return body;
    };
    return this.settleWithStatusHandling(build, '/spin/ticket_spend', 0, 0);
  }

  private async settleWithStatusHandling(
    buildBody: () => Record<string, unknown>,
    path: string,
    staleRetries: number,
    nonceRetries: number,
  ): Promise<Settlement> {
    const body = buildBody();
    const settlement = await this.postSettleWithTransportRetry(path, body);

    switch (settlement.status) {
      case 'REVEALED_SEED':
      case 'STALE_SEED': {
        if (staleRetries >= MAX_STALE_RETRIES) return settlement;
        const fresh = await this.getConfig();
        return this.settleWithStatusHandling(
          () => this.reNonce(buildBody(), fresh),
          path,
          staleRetries + 1,
          nonceRetries,
        );
      }
      case 'NONCE_MISMATCH': {
        if (nonceRetries >= MAX_NONCE_RETRIES) return settlement;
        const fresh = await this.getConfig();
        return this.settleWithStatusHandling(
          () => this.reNonce(buildBody(), fresh),
          path,
          staleRetries,
          nonceRetries + 1,
        );
      }
      default:
        // OK, DAILY_CAP, INSUFFICIENT_FUNDS, INVALID_* — terminal; the caller decides.
        return settlement;
    }
  }

  // Re-point a settle body at the freshly fetched epoch/nonce. For ticket spends that omit
  // epoch/nonce, leave them omitted (hub-core pins the current config itself).
  private reNonce(body: Record<string, unknown>, fresh: SpinConfig): Record<string, unknown> {
    if ('epoch' in body || body.interaction_id === undefined) {
      body.epoch = fresh.epoch;
      body.expected_nonce = fresh.nonce;
    }
    return body;
  }

  // POST a settle/ticket_spend, retrying the SAME body on transport failure only. Returns a
  // parsed HTTP-200 Settlement (any status), or throws UpstreamUnavailableError / AuthError.
  private async postSettleWithTransportRetry(
    path: string,
    body: Record<string, unknown>,
  ): Promise<Settlement> {
    let reconnecting = false;
    for (let attempt = 0; ; attempt++) {
      let res: Response;
      try {
        res = await fetch(`${API_BASE}${path}`, {
          method: 'POST',
          headers: this.authHeaders(),
          body: JSON.stringify(body),
        });
      } catch {
        // Network error — genuinely unknown. Retry the SAME body.
        if (attempt >= MAX_TRANSPORT_RETRIES) {
          throw new UpstreamUnavailableError(() => this.postSettleWithTransportRetry(path, body));
        }
        reconnecting = this.signalReconnecting(reconnecting, attempt);
        await sleep(backoffMs(attempt));
        continue;
      }

      if (res.status === 401) throw new AuthError('unauthorized');

      if (res.status === 429) {
        const retryAfter = await this.retryAfterSeconds(res);
        if (attempt >= MAX_TRANSPORT_RETRIES) {
          throw new UpstreamUnavailableError(() => this.postSettleWithTransportRetry(path, body));
        }
        reconnecting = this.signalReconnecting(reconnecting, attempt);
        await sleep(Math.max(retryAfter * 1000, backoffMs(attempt)));
        continue;
      }

      // The backend already exhausted its own idempotent retries before returning 502/504,
      // so this is genuinely unknown. Retry the SAME body (idempotent upstream).
      if (res.status === 502 || res.status === 504) {
        if (attempt >= MAX_TRANSPORT_RETRIES) {
          throw new UpstreamUnavailableError(() => this.postSettleWithTransportRetry(path, body));
        }
        reconnecting = this.signalReconnecting(reconnecting, attempt);
        await sleep(backoffMs(attempt));
        continue;
      }

      if (!res.ok) throw new Error(`settle failed (${res.status})`);

      if (reconnecting) this.hooks.onReconnected?.();
      return (await res.json()) as Settlement;
    }
  }

  private signalReconnecting(already: boolean, attempt: number): boolean {
    this.hooks.onReconnecting?.(attempt);
    return already || true;
  }

  private async retryAfterSeconds(res: Response): Promise<number> {
    try {
      const body = (await res.clone().json()) as ErrorBody;
      const value = Number(body.retry_after ?? 0);
      if (!Number.isFinite(value) || value < 0) return 0;
      return Math.min(RETRY_AFTER_CAP_S, value);
    } catch {
      return 0;
    }
  }
}
